import _ from 'lodash'
import {
  validateThisOptions,
  getStringLengthValidator,
  getIntRangeValidator
} from 'pw-validators'

const SURICATA_ACTION_OPTIONS = [
  'pass',
  'drop',
  'reject',
  'alert'
]

const SURICATA_PROTO_OPTIONS = [
  'dcerpc',
  'dhcp',
  'dnp3',
  'dns',
  'enip',
  'ftp',
  'ftp-data',
  'http',
  'icmp',
  'icmpv4',
  'icmpv6',
  'ikev2',
  'imap',
  'ip',
  'ip4',
  'ip6',
  'ipv4',
  'ipv6',
  'krb5',
  'modbus',
  'nfs',
  'ntp',
  'pkthdr',
  'sctp',
  'smb',
  'smtp',
  'snmp',
  'ssh',
  'tcp',
  'tcp-pkt',
  'tcp-stream',
  'tftp',
  'tls',
  'udp'
]

const SURICATA_DIR_OPTIONS = [
  '->',
  '<-', // TODO not supported by suricata?
  '<>'  // TODO not supported by suricata?
]

const SURICATA_IPOPTS_OPTIONS = [
  'rr',
  'eol',
  'nop',
  'ts',
  'sec',
  'esec',
  'lsrr',
  'ssrr',
  'satid',
  'any'
]

const MAX_ID = 4294967295
// const COMMON_NET_VARS = [
//   '$HOME_NET',
//   '$EXTERNAL_NET'
// ]
//
// const COMMON_PORT_VARS = [
//   'any',
//   '$EXTERNAL_PORTS'
// ]


const RULE_HEADER = {
  link: "//suricata.readthedocs.io/en/latest/rules/intro.html",
  parts: {
    action: {
      required: true,
      options: SURICATA_ACTION_OPTIONS,
      validate: validateThisOptions,
      desc: `Action determines what will happen when a signature matches. Possible values: ${ SURICATA_ACTION_OPTIONS.join(', ') }`
    },
    protocol: {
      required: true,
      options: SURICATA_PROTO_OPTIONS,
      validate: validateThisOptions,
      desc: `This keyword in a signature tells Suricata which protocol it concerns. Possible values: ${ SURICATA_PROTO_OPTIONS.join(', ') }`
    },
    src_ip: {
      required: true,
      desc: "In Source Net you can assign IP-addresses; IPv4 and IPv6 combined as well as separated. You can also set variables such as HOME_NET.",
      example: `! 1.1.1.1, [$EXTERNAL_NET, !$HOME_NET], [10.0.0.0/24, !10.0.0.5]`
    },
    src_port: {
      required: true,
      desc: "Traffic comes in and goes out through ports. Different ports have different port-numbers. The HTTP-port for example is 80 while 443 is the port for HTTPS. Commonly the Source port will be set as ‘any’.",
      example: `any, !80 , [1024: ]`
    },
    direction: {
      required: true,
      options: SURICATA_DIR_OPTIONS,
      validate: validateThisOptions,
      desc: `The direction tells in which way the signature has to match. Nearly every signature has an arrow to the right. This means that only packets with the same direction can match. Possible Values: ${ SURICATA_DIR_OPTIONS.join(', ') }`
    },
    dst_ip: {
      required: true,
      desc: "In Destination Net you can assign IP-addresses; IPv4 and IPv6 combined as well as separated. You can also set variables such as HOME_NET.",
      example: `! 1.1.1.1, [$EXTERNAL_NET, !$HOME_NET], [10.0.0.0/24, !10.0.0.5]`
    },
    dst_port: {
      required: true,
      desc: "Traffic comes in and goes out through ports. Different ports have different port-numbers. The HTTP-port for example is 80 while 443 is the port for HTTPS. Commonly the Source port will be set as ‘any’.",
      example: `any, !80 , [1024: ]`
    },
  }
}

const META_SETTINGS = {
  link: "//suricata.readthedocs.io/en/latest/rules/meta.html",
  parts: {
    msg: {
      required: true,
      preventMultiple: true,
      validate: getStringLengthValidator(3, 300),
      desc: "The keyword msg gives more information about the signature and the possible alert. The first part shows the class of the signature. It is a convention that part is written in uppercase characters."
    },
    sid: {
      required: true,
      preventMultiple: true,
      validate: getIntRangeValidator(1, MAX_ID),
      desc: "The keyword sid gives every signature its own id."
    },
    gid: {
      preventMultiple: true,
      validate: getIntRangeValidator(1, MAX_ID),
      desc: "The gid keyword can be used to give different groups of signatures another id value (like in sid)."
    },
    rev: {
      preventMultiple: true,
      label: "Revision",
      validate: getIntRangeValidator(0, 2147483647), // < i32
      desc: "Rev represents the version of the signature. If a signature is modified, the number of rev will be incremented automatically."
    },
    classtype: {
      preventMultiple: true,
      desc: "The classtype keyword gives information about the classification of rules and alerts. It consists of a short name, a long name and a priority."
    },
    reference: {
      desc: "The reference keywords direct to places where information about the signature and about the problem the signature tries to address, can be found. The reference keyword can appear multiple times in a signature.",
      example: `reference: url, www.example.com`
    },
    metadata: {
      desc: "Suricata ignores the words behind meta data. Suricata supports this keyword because it is part of the signature language."
    },
    priority: {
      preventMultiple: true,
      validate: getIntRangeValidator(0, 255),
      desc: "The priority keyword comes with a mandatory numeric value which can range from 1 till 255. The numbers 1 to 4 are most often used. Signatures with a higher priority will be examined first. The highest priority is 1."
    }
  }
}

const HEADER_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/header-keywords.html",
  parts: {
    ttl: {
      desc: "The ttl keyword is used to check for a specific IP time-to-live value in the header of a packet. The Time-to-live value determines the maximal amount of time a packet can be in the Internet-system. If this field is set to 0, then the packet has to be destroyed.",
      example: `ttl:10;`
    },
    ipopts: {
      options: SURICATA_IPOPTS_OPTIONS,
      validate: validateThisOptions,
      desc: `With the ipopts keyword you can check if a specific ip option is set. Ipopts has to be used at the beginning of a rule. You can only match on one option per rule. There are several options on which can be matched. Possible values: ${ SURICATA_IPOPTS_OPTIONS.join(', ') }`,
    },
    sameip: {
      keyOnly: true,
      desc: `Every packet has a source IP-address and a destination IP-address. It can be that the source IP is the same as the destination IP. With the sameip keyword you can check if the IP address of the source is the same as the IP address of the destination.`,
    },
    ip_proto: {
      desc: `With the ip_proto keyword you can match on the IP protocol in the packet-header. You can use the name or the number of the protocol.`,
    },
    id: {
      desc: `With the id keyword, you can match on a specific IP ID value. The ID identifies each packet sent by a host and increments usually with one with each packet that is being send. The IP ID is uses as a fragment identification number. Each packet has an IP ID, and when the packet becomes fragmented, all fragments of this packet have the same ID. In this way, the receiver of the packet knows which fragments belong to the same packet.`,
    },
    geoip: {
      desc: `The geoip keyword enables (you) to match on the source, destination or source and destination IP addresses of network traffic, and to see to which country it belongs.`,
      example: `geoip: src, RU;`
    },
    fragbits: {
      desc: `With the fragbits keyword, you can check if the fragmentation and reserved bits are set in the IP header. The fragbits keyword should be placed at the beginning of a rule. Fragbits is used to modify the fragmentation mechanism.`,
    },
    fragoffset: {
      desc: `With the fragoffset keyword you can match on specific decimal values of the IP fragment offset field.`,
    },
    seq: {
      desc: `The seq keyword can be used in a signature to check for a specific TCP sequence number. A sequence number is a number that is generated practically at random by both endpoints of a TCP-connection. The client and the server both create a sequence number, which increases with one with every byte that they send. So this sequence number is different for both sides.`,
    },
    ack: {
      desc: `The ack is the acknowledgement of the receipt of all previous (data)-bytes send by the other side of the TCP-connection.`,
    },
    window: {
      desc: `The window keyword is used to check for a specific TCP window size. The TCP window size is a mechanism that has control of the data-flow. The window is set by the receiver (receiver advertised window size) and indicates the amount of bytes that can be received.`,
    },
    itype: {
      desc: `The itype keyword is for matching on a specific ICMP type (number). ICMP has several kinds of messages and uses codes to clarify those messages.`,
    },
    icode: {
      desc: `With the icode keyword you can match on a specific ICMP code. The code of a ICMP message clarifies the message.`,
    },
    icmp_id: {
      desc: `With the icmp_id keyword you can match on specific ICMP id-values. Every ICMP-packet gets an id when it is being sent.`,
    },
    icmp_seq: {
      desc: `You can use the icmp_seq keyword to check for a ICMP sequence number. ICMP messages all have sequence numbers. This can be useful (together with the id) for checking which reply message belongs to which request message.`,
    },
  }
}

const PREFILTER = {
  link: "//suricata.readthedocs.io/en/latest/rules/prefilter.html",
  parts: {
    prefilter: {
      keyOnly: true,
      desc: `The prefilter engines for other non-MPM keywords can be enabled in specific rules by using the ‘prefilter’ keyword.`,
    }
  }
}

const PAYLOAD_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/payload-keywords.html",
  parts: {
    content: {
      desc: `The content keyword is very important in signatures. Between the quotation marks you can write on what you would like the signature to match. It is possible to use several contents in a signature.`
    },
    nocase: {
      keyOnly: true,
      desc: `If you do not want to make a distinction between uppercase and lowercase characters, you can use nocase. The keyword nocase is a content modifier.`
    },
    depth: {
      desc: `The depth keyword is a absolute content modifier. It comes after the content. The depth content modifier comes with a mandatory numeric value.`
    },
    offset: {
      desc: `The offset keyword designates from which byte in the payload will be checked to find a match. For instance offset:3; checks the fourth byte and further.`
    },
    distance: {
      desc: `The keyword distance is a relative content modifier. This means it indicates a relation between this content keyword and the content preceding it. Distance has its influence after the preceding match. The keyword distance comes with a mandatory numeric value.`
    },
    within: {
      desc: `The keyword within is relative to the preceding match. The keyword within comes with a mandatory numeric value. Using within makes sure there will only be a match if the content matches with the payload within the set amount of bytes.`
    },
    isdataat: {
      desc: `The purpose of the isdataat keyword is to look if there is still data at a specific part of the payload. The keyword starts with a number (the position) and then optional followed by ‘relative’ separated by a comma and the option rawbytes.`
    },
    dsize: {
      desc: `With the dsize keyword, you can match on the size of the packet payload. You can use the keyword for example to look for abnormal sizes of payloads. This may be convenient in detecting buffer overflows.`
    },
    rpc: {
      desc: `The rpc keyword can be used to match in the SUNRPC CALL on the RPC procedure numbers and the RPC version.`
    },
    replace: {
      desc: `The replace content modifier can only be used in ips. It adjusts network traffic. It changes the content it follows (‘abc’) into another (‘def’)`
    },
    pcre: {
      desc: `(Perl Compatible Regular Expressions) The keyword pcre matches specific on regular expressions. `
    },
    fast_pattern: {
      keyOnly: true, // key-only is OPTIONAL for fast_pattern
      desc: `Only one content of a signature will be used in the Multi Pattern Matcher (MPM). If there are multiple contents, then Suricata uses the ‘strongest’ content. This means a combination of length, how varied a content is, and what buffer it is looking in. Generally, the longer and more varied the better.`
    }
  }
}


const HTTP_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/http-keywords.html",
  parts: {
    http_method: {
      desc: `With the http_method content modifier, it is possible to match specifically and only on the HTTP method buffer. The keyword can be used in combination with all previously mentioned content modifiers such as: depth, distance, offset, nocase and within.`
    },
    http_uri: {
      desc: `With the http_uri content modifier, it is possible to match specifically and only on the request URI buffer.`
    },
    http_raw_uri: {
      desc: `With the http_raw_uri content modifier, it is possible to match specifically and only on the request URI buffer.`
    },
    uricontent: {
      desc: `The uricontent keyword has the exact same effect as the http_uri content modifier. uricontent is a deprecated (although still supported) way to match specifically and only on the request URI buffer.`
    },
    urilen: {
      desc: `The urilen keyword is used to match on the length of the request URI. It is possible to use the < and > operators, which indicate respectively smaller than and larger than.`
    },
    http_request_line: {
      desc: `The http_request_line forces the whole HTTP request line to be inspected.`
    },
    http_header: {
      desc: `With the http_header content modifier, it is possible to match specifically and only on the HTTP header buffer. This contains all of the extracted headers in a single buffer, except for those indicated in the documentation that are not able to match by this buffer and have their own content modifier (e.g. http_cookie).`
    },
    http_raw_header: {
      desc: `With the http_raw_header content modifier, it is possible to match specifically and only on the HTTP header buffer. This contains all of the extracted headers in a single buffer, except for those indicated in the documentation that are not able to match by this buffer and have their own content modifier (e.g. http_cookie).`
    },
    http_cookie: {
      desc: `With the http_cookie content modifier, it is possible to match specifically and only on the cookie buffer.`
    },
    http_user_agent: {
      desc: `The http_user_agent content modifier is part of the HTTP request header. It makes it possible to match specifically on the value of the User-Agent header.`
    },
    http_client_body: {
      desc: `With the http_client_body content modifier, it is possible to match specifically and only on the HTTP request body.`
    },
    http_stat_code: {
      desc: `With the http_stat_code content modifier, it is possible to match specifically and only on the HTTP status code buffer.`
    },
    http_stat_msg: {
      desc: `With the http_stat_msg content modifier, it is possible to match specifically and only on the HTTP status message buffer. `
    },
    http_response_line: {
      desc: `The http_response_line forces the whole HTTP response line to be inspected.`
    },
    http_server_body: {
      desc: `With the http_server_body content modifier, it is possible to match specifically and only on the HTTP response body.`
    },
    http_host: {
      desc: `With the http_host content modifier, it is possible to match specifically and only the normalized hostname.`
    },
    http_raw_host: {
      desc: `With the http_raw_host content modifier, it is possible to match specifically and only the raw hostname.`
    },
    file_data: {
      desc: `With file_data, the HTTP response body is inspected, just like with http_server_body. The file_data keyword works a bit differently from the normal content modifiers; when used in a rule, all content matches following it in the rule are affected (modified) by it.`
    },
  }
}




const FLOW_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/flow-keywords.html",
  parts: {
    flowbits: {
      desc: `Flowbits consists of two parts. The first part describes the action it is going to perform, the second part is the name of the flowbit.`,
      example: `flowbits: set, name`
    },
    flow: {
      desc: `The flow keyword can be used to match on direction of the flow, so to/from client or to/from server. It can also match if the flow is established or not. The flow keyword can also be use to say the signature has to match on stream only (only_stream) or on packet only (no_stream).`,
      example: `flow:to_server, established, only_stream`
    },
    stream_size: {
      desc: `The stream size option matches on traffic according to the registered amount of bytes by the sequence numbers.`
    }
  }
}


const FLOWINT = {
  link: "//suricata.readthedocs.io/en/latest/rules/flowint.html",
  parts: {
    flowint: {
      desc: `Flowint allows storage and mathematical operations using variables. It operates much like flowbits but with the addition of mathematical capabilities and the fact that an integer can be stored and manipulated, not just a flag set.`,
      example: `flowint:loginfail, +, 1;`
    },
  }
}

const XBITS = {
  link: "//suricata.readthedocs.io/en/latest/rules/xbits.html",
  parts: {
    xbits: {
      desc: `Set, unset, toggle and check for bits stored per host or ip_pair.`
    },
  }
}

const FILE_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/file-keywords.html",
  parts: {
    filename: {
      desc: `Matches on the file name.`
    },
    fileext: {
      desc: `Matches on the extension of a file name.`
    },
    filemagic: {
      desc: `Matches on the information libmagic returns about a file.`
    },
    filestore: {
      desc: `Stores files to disk if the signature matched.`
    },
    filemd5: {
      desc: `Match file MD5 against list of MD5 checksums.`
    },
    filesize: {
      desc: `Match on the size of the file as it is being transferred.`
    },
  }
}

const RULE_THRESHOLDING = {
  link: "//suricata.readthedocs.io/en/latest/rules/thresholding.html",
  parts: {
    threshold: {
      desc: `The threshold keyword can be used to control the rule’s alert frequency. It has 3 modes: threshold, limit and both.`
    },
    detection_filter: {
      desc: `The detection_filter keyword can be used to alert on every match after a threshold has been reached.`
    }
  }
}

const DNS_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/dns-keywords.html",
  parts: {
    dns_query: {
      desc: `With dns_query the DNS request queries are inspected. The dns_query keyword works a bit different from the normal content modifiers. When used in a rule all contents following it are affected by it.`
    },
  }
}

const SSLTLS_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/tls-keywords.html",
  parts: {
    tls_cert_subject: {
      desc: `Match TLS/SSL certificate Subject field.`
    },
    tls_cert_issuer: {
      desc: `Match TLS/SSL certificate Issuer field.`
    },
    tls_cert_notbefore: {
      desc: `Match on the NotBefore field in a certificate.`
    },
    tls_cert_notafter: {
      desc: `Match on the NotAfter field in a certificate.`
    },
    tls_cert_expired: {
      desc: `Match returns true if certificate is expired. It evaluates the validity date from the certificate.`
    },
    tls_cert_valid: {
      desc: `Match returns true if certificate is not expired. It only evaluates the validity date. It does not do cert chain validation. It is the opposite of tls_cert_expired.`
    },
    "tls.version": {
      desc: `Match on negotiated TLS/SSL version.`
    },
    "tls.subject": {
      desc: `Match TLS/SSL certificate Subject field.`
    },
    "tls.issuerdn": {
      desc: `match TLS/SSL certificate IssuerDN field`
    },
    "tls.fingerprint": {
      desc: `match TLS/SSL certificate SHA1 fingerprint`
    },
    "tls.store": {
      desc: `store TLS/SSL certificate on disk`
    },
    ssl_state: {
      desc: `The ssl_state keyword matches the state of the SSL connection. The possible states are client_hello, server_hello, client_keyx, server_keyx and unknown. You can specify several states with | (OR) to check for any of the specified states.`
    },
  }
}

const MODBUS_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/modbus-keywords.html",
  parts: {
    modbus: {
      desc: `The modbus keyword can be used for matching on various properties of Modbus requests. There are two ways of using this keyword: matching on functions properties with the setting “function”, and matching on directly on data access with the setting “access”.`
    },
  }
}

const DNP_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/dnp3-keywords.html",
  parts: {
    dnp3_func: {
      desc: `This keyword will match on the application function code found in DNP3 request and responses. It can be specified as the integer value or the symbolic name of the function code.`
    },
    dnp3_ind: {
      desc: `This keyword matches on the DNP3 internal indicator flags in the response application header.`
    },
    dnp3_obj: {
      desc: `This keyword matches on the DNP3 application data objects.`
    },
    dnp3_data: {
      desc: `This keyword will cause the following content options to match on the re-assembled application buffer. The reassembled application buffer is a DNP3 fragment with CRCs removed (which occur every 16 bytes), and will be the complete fragment, possibly reassembled from multiple DNP3 link layer frames.`
    },
  }
}

// const ENIPCIP_KEYWORDS = {
//   link: "//suricata.readthedocs.io/en/latest/rules/enip-keywords.html",
//   parts: {
//     temp: {
//       desc: ``
//     },
//   }
// }

const APP_LAYER_KEYWORDS = {
  link: "//suricata.readthedocs.io/en/latest/rules/app-layer.html",
  parts: {
    "app-layer-protocol": {
      desc: `Match on the detected app-layer protocol. A special value ‘failed’ can be used for matching on flows in which protocol detection failed.`,
      example: `app-layer-protocol:ssh;`
    },
    "app-layer-event": {
      desc: `Match on events generated by the App Layer Parsers and the protocol detection engine.`
    },
  }
}

const MISC_KEYWORDS = {
  link: "//manual-snort-org.s3-website-us-east-1.amazonaws.com/node32.html#SECTION004531000000000000000",
  parts: {
    "byte_test": {
      desc: `Test a byte field against a specific value (with operator). Capable of testing binary values or converting representative byte strings to their binary equivalent and testing them.`,
      example: `byte_test:4, >, 1000, 20, relative;`
    },
    "byte_jump": {
      desc: `The byte_jump keyword allows rules to be written for length encoded protocols trivially. By having an option that reads the length of a portion of data, then skips that far forward in the packet, rules can be written that skip over specific portions of length-encoded protocols and perform detection in very specific locations.`,
      example: `byte_jump:4, 12, relative, align;`
    },
    "byte_extract": {
      desc: `The byte_extract keyword is another useful option for writing rules against length-encoded protocols. It reads in some number of bytes from the packet payload and saves it to a variable. These variables can be referenced later in the rule, instead of using hard-coded values.`,
      example: `byte_extract:1, 1, str_depth;`
    }
  }
}





const requiredFieldNames = []
const keyOnlyFieidNames = []
const contentMatchingFieldNames = []

// Header schema is based only on index-order
export const RULE_HEADER_SCHEMA = _.map(RULE_HEADER.parts, (headerPart, headerPartFieldName) => {
  requiredFieldNames.push(headerPartFieldName) // All required
  headerPart.fieldName = headerPartFieldName
  headerPart.className = `rule_part rule_header_part rule_part_${ _.snakeCase(headerPartFieldName) }`
  headerPart.tooltip = headerPart.desc
  return headerPart
})

const BASE_VALID_PARTS = [
  'app-layer-event',
  'app-layer-protocol',
  'asn1',
  'base64_data',
  'base64_decode',
  'bsize',
  'bypass',
  'byte_extract',
  'byte_jump',
  'byte_test',
  'cip_service',
  'classtype',
  'compress_whitespace',
  'content',
  'datarep',
  'dataset',
  'dcerpc.iface',
  'dcerpc.opnum',
  'dcerpc.stub_data',
  'decode-event',
  'depth',
  'detection_filter',
  'distance',
  'dnp3.data',
  'dnp3_func',
  'dnp3_ind',
  'dnp3_obj',
  'dns.opcode',
  'dns.query',
  'dotprefix',
  'dsize',
  'endswith',
  'engine-event',
  'enip_command',
  'fast_pattern',
  'file.data',
  'fileext',
  'filemagic',
  'file.magic',
  'filemd5',
  'filename',
  'file.name',
  'filesha1',
  'filesha256',
  'filesize',
  'filestore',
  'flow',
  'flowbits',
  'flowint',
  'flowvar',
  'fragbits',
  'fragoffset',
  'ftpbounce',
  'ftpdata_command',
  'geoip',
  'gid',
  'hostbits',
  'http.accept',
  'http.accept_enc',
  'http.accept_lang',
  'http_client_body',
  'http.connection',
  'http.content_len',
  'http.content_type',
  'http_cookie',
  'http.cookie',
  'http_header',
  'http.header',
  'http.header_names',
  'http.header.raw',
  'http_host',
  'http.host',
  'http.host.raw',
  'http.location',
  'http_method',
  'http.method',
  'http.protocol',
  'http_raw_header',
  'http_raw_host',
  'http_raw_uri',
  'http.referer',
  'http.request_body',
  'http.request_line',
  'http.response_body',
  'http.response_line',
  'http.server',
  'http_server_body',
  'http.start',
  'http_stat_code',
  'http.stat_code',
  'http_stat_msg',
  'http.stat_msg',
  'http_uri',
  'http.uri',
  'http.uri.raw',
  'http_user_agent',
  'http.user_agent',
  'icmp_id',
  'icmp_seq',
  'icmpv4-csum',
  'icmpv6-csum',
  'icode',
  'id',
  'ipopts',
  'ip_proto',
  'iprep',
  'ipv4-csum',
  'ipv4.hdr',
  'ipv6.hdr',
  'isdataat',
  'itype',
  'ja3.hash',
  'ja3s.hash',
  'ja3s.string',
  'ja3.string',
  'krb5.cname',
  'krb5_err_code',
  'krb5_msg_type',
  'krb5.sname',
  'l3_proto',
  'metadata',
  'modbus',
  'msg',
  'nfq_set_mark',
  'nfs_procedure',
  'nfs.version',
  'noalert',
  'nocase',
  'offset',
  'pcre',
  'pkt_data',
  'pktvar',
  'prefilter',
  'priority',
  'rawbytes',
  'reference',
  'replace',
  'rev',
  'rpc',
  'sameip',
  'sid',
  'sip.method',
  'sip.protocol',
  'sip.request_line',
  'sip.response_line',
  'sip.stat_code',
  'sip.stat_msg',
  'sip.uri',
  'smb.named_pipe',
  'smb.share',
  'snmp.community',
  'snmp.pdu_type',
  'snmp.version',
  'ssh.proto',
  'ssh.protoversion',
  'ssh.software',
  'ssh.softwareversion',
  'ssl_state',
  'ssl_version',
  'startswith',
  'stream-event',
  'stream_size',
  'strip_whitespace',
  'tag',
  'target',
  'tcp.ack',
  'tcp.flags',
  'tcp.hdr',
  'tcp.mss',
  'tcp.seq',
  'tcpv4-csum',
  'tcpv6-csum',
  'tcp.window',
  'template2',
  'threshold',
  'tls_cert_expired',
  'tls.cert_fingerprint',
  'tls.cert_issuer',
  'tls_cert_notafter',
  'tls_cert_notbefore',
  'tls.certs',
  'tls.cert_serial',
  'tls.cert_subject',
  'tls_cert_valid',
  'tls.fingerprint',
  'tls.issuerdn',
  'tls.sni',
  'tls_store',
  'tls.subject',
  'tls.version',
  'to_md5',
  'tos',
  'to_sha1',
  'to_sha256',
  'ttl',
  'udp.hdr',
  'udpv4-csum',
  'udpv6-csum',
  'uricontent',
  'urilen',
  'within',
  'xbits'
].reduce((o, k) => ({
  ...o,
  [k]: {
    fieldName: k,
    className: `rule_part rule_option rule_option_uncategorized rule_part_${_.snakeCase(k)}`,
    tooltip: ''
  }
}), {})

// Rule Options are key:value
export const RULE_OPTION_SCHEMA = _.reduce({
  // RULE_HEADER,
  META_SETTINGS,
  HEADER_KEYWORDS,
  PREFILTER,
  PAYLOAD_KEYWORDS,
  HTTP_KEYWORDS,
  FLOW_KEYWORDS,
  FLOWINT,
  XBITS,
  FILE_KEYWORDS,
  RULE_THRESHOLDING,
  DNS_KEYWORDS,
  SSLTLS_KEYWORDS,
  MODBUS_KEYWORDS,
  DNP_KEYWORDS,
  // ENIPCIP_KEYWORDS,
  APP_LAYER_KEYWORDS,
  MISC_KEYWORDS
}, (out, section, sectionName) => {
  const sectionClassName = sectionName.toLowerCase()
  _.forEach(section.parts, (part, partFieldName) => {
    out[partFieldName] = _.assign({
      fieldName: partFieldName,
      className: `rule_part rule_option ${ sectionClassName } rule_part_${ _.snakeCase(partFieldName) } ${ part.keyOnly ? "key_only_allowed" : "" }`,
      tooltip: part.desc
    }, part)
    if (sectionName !== "META_SETTINGS") {
      contentMatchingFieldNames.push(partFieldName)
    }
    if (part.keyOnly) {
      keyOnlyFieidNames.push(partFieldName)
    }
    if (part.required) {
      requiredFieldNames.push(partFieldName)
    }
  })
  return out
}, BASE_VALID_PARTS)

export const REQUIRED_FIELD_NAMES = requiredFieldNames
export const KNOWN_KEY_ONLY_FIELD_NAMES = keyOnlyFieidNames
export const CONTENT_MATCHING_FIELD_NAMES = contentMatchingFieldNames
